import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Icon from 'assets/components/icon/Icon';
import Page, { PageClasses, PageStructure } from 'assets/components/page/Page';
import ThemePageSplit from 'assets/components/page/themes/Theme.Page.Split';
import { getLocales } from 'assets/locales/Locale';
import ProductPricingButton from 'module/productManagement/components/productPricingButton/ProductPricingButton';
import { ProductAction, ProductEditTab, useProductProvider } from '../Product.Hooks';
import ProductEditDocuments from './edit/documents/Product.Edit.Documents';
import ProductEditInfo from './edit/Product.Edit.Info';
import ProductEditNavigation from './edit/Product.Edit.Navigation';
import ProductEditProvinceGroups from './edit/Product.Edit.ProvinceGroups';
import ProductEditTaxes from './edit/Product.Edit.Taxes';
import ProductEditDocumentTemplates from './edit/Product.Edit.DocumentTemplates';
import ProductPricingAdjustmentButton from 'module/productManagement/components/productPricingButton/ProductPricingAdjustmentButton';
import ProductEditPricingAutomationRules from './edit/Product.Edit.PricingAutomationRules';
import ProductReportSettings from './edit/Product.ReportSettings';
import { useEffect } from 'react';
import { resetScroll } from 'assets/utils/dom/Scroll';
import ProductEditRefundDocuments from './edit/documents/Product.Edit.RefundDocuments';
import ProductEditTransferOwnershipDocuments from './edit/documents/Product.Edit.TransferOwnershipDocuments';

export default function ProductEdit() {
  const { lang } = getLocales();
  const {
    pageRouter,
    form,
    product,
    isEditLoading,
    newProductDocuments,
    newProductRefundDocuments,
    newProductTransferOwnershipDocuments,
    permissions,
  } = useProductProvider();

  useEffect(() => {
    if (pageRouter.query.editTab) {
      const container = document.querySelector<HTMLElement>(
        `.${PageStructure.page}.${PageClasses.ThemePageSplit} .${PageStructure.pageSidebar}`
      );
      if (container) resetScroll(container);
    }
  }, [pageRouter.query.editTab]);

  return (
    <Page
      class={ThemePageSplit}
      header={
        <h3>
          {lang.addNew} {lang.product}
        </h3>
      }
      isLoading={isEditLoading}
      sidebar={
        <>
          {(pageRouter.query.editTab === ProductEditTab.INFO || !pageRouter.query.editTab) && <ProductEditInfo />}
          {pageRouter.query.editTab === ProductEditTab.DOCUMENTS && <ProductEditDocuments />}
          {pageRouter.query.editTab === ProductEditTab.REFUND_DOCUMENTS && <ProductEditRefundDocuments />}
          {pageRouter.query.editTab === ProductEditTab.TRANSFER_OWNERSHIP_DOCUMENTS && (
            <ProductEditTransferOwnershipDocuments />
          )}
          {pageRouter.query.editTab === ProductEditTab.DOC_TEMPLATES && <ProductEditDocumentTemplates />}
          {pageRouter.query.editTab === ProductEditTab.TAXES && permissions?.EDIT_PRODUCTTAX && <ProductEditTaxes />}
          {pageRouter.query.editTab === ProductEditTab.PROVINCE_GROUPS && <ProductEditProvinceGroups />}
          {pageRouter.query.editTab === ProductEditTab.PRICING_AUTOMATION_RULES && (
            <ProductEditPricingAutomationRules />
          )}
          {pageRouter.query.editTab === ProductEditTab.REPORT_SETTINGS && <ProductReportSettings />}
        </>
      }
      render={<ProductEditNavigation />}
      footer={
        <>
          <ProductPricingAdjustmentButton
            id={product?.id}
            dataType="product"
            productType={product?.productType}
            label={lang.adjustments}
          />
          <ProductPricingButton product={product} label={lang.goToPrices} />
          <Button
            onClick={form.submit}
            media={<Icon class="fas-save" />}
            render={
              <>
                {lang.save}
                {newProductDocuments.length > 0 && (
                  <>
                    &nbsp;+&nbsp;{newProductDocuments.length}
                    &nbsp;
                    <Icon class="fas-paperclip" />
                  </>
                )}
                {newProductRefundDocuments.length > 0 && (
                  <>
                    &nbsp;+&nbsp;{newProductRefundDocuments.length}
                    &nbsp;
                    <Icon class="fas-paperclip" />
                  </>
                )}
                {newProductTransferOwnershipDocuments.length > 0 && (
                  <>
                    &nbsp;+&nbsp;{newProductTransferOwnershipDocuments.length}
                    &nbsp;
                    <Icon class="fas-paperclip" />
                  </>
                )}
              </>
            }
            active={!form.hasErrors && form.isUpdated}
            disabled={form.hasErrors || !form.isUpdated}
            isLoading={form.isSubmitted}
            class={ThemeButtonCircle}
          />
          {product && (
            <Button
              onClick={pageRouter.updateParams}
              data={{
                id: product?.id?.toString(),
                action: ProductAction.DELETE,
              }}
              class={ThemeButtonCircle}
              disabled={!product.id}
              render={lang.delete}
              media={<Icon class="fas-trash" />}
            />
          )}
        </>
      }
    />
  );
}
